/**
 * Sprint progression helpers · linear-locking model.
 * Per Bug 1+3 fix · 2 MAY 2026: студент не должен прыгать на S02 пока S01 не done.
 *
 * "Required tickets" = все ticket типы кроме `mission` (per Bug 4 / Final test gate).
 * Sprint is **complete** когда все non-mission tickets в нём = 'done'.
 * Active sprint = первый ОБЯЗАТЕЛЬНЫЙ sprint в порядке `ord`, который ещё не complete
 * (либо optional-спринт, в котором студент сейчас работает — см. activeSprint).
 * Locked sprint = любой sprint после первого incomplete ОБЯЗАТЕЛЬНОГО.
 * Optional-спринты (S06): не блокируют, не блокируются «вперёд», открываются
 * вместе со следующим обязательным (slot_s06_rework · 11 JUN 2026).
 */

import type { Progress, TicketMeta, Sprint } from '@/types';
import { SPRINTS } from '@shared/data/sprints';
import { ticketById, ticketsBySprint } from '@shared/data/tickets';
// SSOT (29 JUN · slot_content_protection): isSprintComplete / isSprintLocked moved
// to shared/access/entitlement so the edge content gate shares identical rules.
// This file keeps the client-only wrapper (dev unlock-all from localStorage) +
// the client navigation helpers (activeSprint / nextTicketForSprint / etc.).
import { isSprintComplete, isSprintLocked as isSprintLockedCore } from '@shared/access/entitlement';

export { isSprintComplete } from '@shared/access/entitlement';

const isMissionTicket = (t: TicketMeta): boolean => t.type === 'mission';

const requiredTicketsForSprint = (sprintId: string): TicketMeta[] =>
  ticketsBySprint(sprintId).filter((t) => !isMissionTicket(t));

/** Compute the % of required tickets done in a given sprint. */
export function sprintRequiredPct(progress: Progress, sprintId: string): number {
  const required = requiredTicketsForSprint(sprintId);
  if (required.length === 0) return 0;
  const done = required.filter((t) => progress.tickets[t.id]?.status === 'done').length;
  return Math.round((done / required.length) * 100);
}

// isSprintComplete now lives in shared/access/entitlement (SSOT · re-exported above).

/** First REQUIRED sprint not yet complete. Falls back to the last sprint when all done.
 *
 *  slot_s06_rework (11 JUN 2026): optional-спринты (S06 · сертификация) пропускаются —
 *  иначе студент, отложивший S06, навсегда «застревал» бы в нём как в активном.
 *  Исключение: если ПОСЛЕДНИЙ тронутый тикет лежит в открытом незавершённом
 *  optional-спринте — студент работает именно там, он и есть активный (так resume,
 *  Dashboard hero и карта остаются согласованы между собой). */
export function activeSprint(progress: Progress): Sprint {
  const lastId = progress.last.ticketId;
  if (lastId) {
    const meta = ticketById(lastId);
    const sp = meta ? SPRINTS.find((s) => s.id === meta.sprintId) : undefined;
    if (
      sp?.optional &&
      !isSprintComplete(progress, sp.id) &&
      !isSprintLocked(progress, sp.id)
    ) {
      return sp;
    }
  }
  const found = SPRINTS.find((s) => !s.optional && !isSprintComplete(progress, s.id));
  return found ?? SPRINTS[SPRINTS.length - 1]!;
}

/** Dev-only override: unlock every sprint regardless of progression.
 *  Toggled via Topbar `DevToggle` pill · keyed in raw localStorage.
 *  Value `'1'` = unlocked-all · anything else (incl. missing) = normal locking. */
export const DEV_UNLOCK_KEY = 'qastudio.v1.dev.unlockAll';

export function isDevUnlockAll(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(DEV_UNLOCK_KEY) === '1';
  } catch {
    return false;
  }
}

/** True when a sprint is locked behind any earlier incomplete REQUIRED sprint.
 *  Client wrapper around the shared SSOT rule (slot_content_protection · 29 JUN):
 *  feeds the client-only dev unlock-all flag (localStorage) into the pure core.
 *  Server-side, the edge calls the core with devUnlockAll=false. */
export function isSprintLocked(progress: Progress, sprintId: string): boolean {
  return isSprintLockedCore(progress, sprintId, isDevUnlockAll());
}

/** Pick the ticket the user should land on when entering a sprint:
 *  prefer the last-touched ticket if it belongs to this sprint AND isn't done,
 *  otherwise the first ticket whose status !== 'done', otherwise the first ticket. */
export function nextTicketForSprint(progress: Progress, sprintId: string): string | undefined {
  const tickets = ticketsBySprint(sprintId);
  if (tickets.length === 0) return undefined;
  const lastId = progress.last.ticketId;
  if (lastId) {
    const last = tickets.find((t) => t.id === lastId);
    if (last && progress.tickets[lastId]?.status !== 'done') return lastId;
  }
  const firstNotDone = tickets.find((t) => progress.tickets[t.id]?.status !== 'done');
  return firstNotDone?.id ?? tickets[0]?.id;
}

/** Convenience: ticket id для resume на Dashboard / Morning hero. */
export function resumeTicketId(progress: Progress): string | undefined {
  const active = activeSprint(progress);
  return nextTicketForSprint(progress, active.id);
}
